import React, { useMemo } from 'react';
import { BaseProxyProvider } from './baseProxyProvider';
import {
  useFormControllerState,
  useFormWatch,
  getValueFromFormState,
} from '../../../../../helpers/formManager';
import {
  fieldNames,
  fieldPathBuilders,
} from '../../../shared/configs/acceptanceForm';
import { dateFormats } from '../../../shared/packages/dateTime';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { createDefaultNumber } from '../../../services/desk';

const DeskProxyProvider = ({ index, children }) => {
  const observableProps = getObservableProps(index);

  // title
  const deskName = useFormWatch(observableProps.name);

  const title = useDebouncedValue(deskName?.label);

  // dateRange
  const uniqueDateRange = useFormWatch(observableProps.uniqueDateRange);

  const [startDate, endDate] = useFormWatch(
    getRangeFieldProps(uniqueDateRange, index)
  );

  const dateRange = useMemo(
    () =>
      startDate && endDate
        ? `${startDate.format(dateFormats.DMY)} — ${endDate.format(
            dateFormats.DMY
          )}`
        : undefined,
    [startDate, endDate]
  );

  // errors
  const { errors } = useFormControllerState(observableProps.desk);
  const hasErrors = Boolean(
    getValueFromFormState(errors, observableProps.desk.name)
  );

  const value = useMemo(
    () => ({
      title: title?.trim() || createDefaultNumber(index),
      description: dateRange,
      isError: hasErrors,
    }),
    [title, dateRange, hasErrors, index]
  );

  return <BaseProxyProvider value={value}>{children}</BaseProxyProvider>;
};

export default DeskProxyProvider;

function getObservableProps(index) {
  return {
    name: { name: fieldPathBuilders.desk.name(index), exact: true },
    uniqueDateRange: {
      name: fieldPathBuilders.desk.uniqueDateRange(index),
      exact: true,
    },
    desk: { name: fieldPathBuilders.desk.byIndex(index) },
  };
}

function getRangeFieldProps(uniqueDateRange, index) {
  const name = uniqueDateRange
    ? [
        fieldPathBuilders.deskDateRange.fromDate(index, 0),
        fieldPathBuilders.deskDateRange.toDate(index, 0),
      ]
    : [fieldNames.main.fromDate, fieldNames.main.toDate];

  return { name };
}
